import { useState } from "react";
import api from "../api";
import { useConfirm } from "../components/ConfirmDialog";
import ActivityFeed from "../components/ActivityFeed";
import { nodesForPhase } from "../components/workplan/util";

export function wsProgress(ws) {
  if (typeof ws.progress === "number") return ws.progress;
  if (ws.status === "done") return 100;
  if (ws.status === "progress") return 50;
  return 0;
}

export function phasePct(workstreams) {
  if (!workstreams || workstreams.length === 0) return 0;
  const total = workstreams.reduce((acc, w) => acc + wsProgress(w), 0);
  return Math.round(total / workstreams.length);
}

const LEVEL_LABELS = {
  "a-green": "Green · Modernization",
  "a-blue": "Blue · New venture",
  "a-amber": "Amber · Energizing bet",
  "a-red": "Red · Critical",
};

export default function HomeView({ state, enterPhase, isAdmin, onChange, onJump }) {
  const confirm = useConfirm();
  const pct = phasePct(state.workstreams);

  // Local edit state per section.
  const [editingCore, setEditingCore] = useState(false);
  const [coreDraft, setCoreDraft] = useState(state.coreQuestion || "");

  const [editOppIdx, setEditOppIdx] = useState(null);
  const [oppDraft, setOppDraft] = useState({ lead: "", body: "", level: "a-blue" });

  const [editMileIdx, setEditMileIdx] = useState(null);
  const [mileDraft, setMileDraft] = useState({ date: "", text: "" });

  const [newOpp, setNewOpp] = useState(false);
  const [newOppDraft, setNewOppDraft] = useState({ lead: "", body: "", level: "a-blue" });
  const [newMile, setNewMile] = useState(false);
  const [newMileDraft, setNewMileDraft] = useState({ date: "", text: "" });

  // -------- Core question --------
  const saveCore = async () => {
    const { data } = await api.put("/core-question", { text: coreDraft });
    onChange({ ...state, coreQuestion: data.text });
    setEditingCore(false);
  };

  // -------- Opportunities --------
  const opportunities = state.opportunities || [];
  const startEditOpp = (i) => {
    setEditOppIdx(i);
    setOppDraft({ ...opportunities[i] });
  };
  const saveOpp = async () => {
    const { data } = await api.patch(`/opportunities/${editOppIdx}`, oppDraft);
    const next = [...opportunities];
    next[editOppIdx] = { ...next[editOppIdx], ...data };
    onChange({ ...state, opportunities: next });
    setEditOppIdx(null);
  };
  const delOpp = async (i) => {
    const o = opportunities[i];
    const ok = await confirm({
      title: "Delete this opportunity?",
      message: `"${o.lead}"\n\nThis cannot be undone.`,
    });
    if (!ok) return;
    await api.delete(`/opportunities/${i}`);
    onChange({ ...state, opportunities: opportunities.filter((_, j) => j !== i) });
  };
  const addOpp = async () => {
    if (!newOppDraft.lead.trim()) return;
    const { data } = await api.post("/opportunities", newOppDraft);
    onChange({ ...state, opportunities: [...opportunities, data] });
    setNewOpp(false);
    setNewOppDraft({ lead: "", body: "", level: "a-blue" });
  };

  // -------- Milestones --------
  const milestones = state.milestones || [];
  const startEditMile = (i) => {
    setEditMileIdx(i);
    setMileDraft({ ...milestones[i] });
  };
  const saveMile = async () => {
    const { data } = await api.patch(`/milestones/${editMileIdx}`, mileDraft);
    const next = [...milestones];
    next[editMileIdx] = { ...next[editMileIdx], ...data };
    onChange({ ...state, milestones: next });
    setEditMileIdx(null);
  };
  const delMile = async (i) => {
    const m = milestones[i];
    const ok = await confirm({
      title: "Delete this milestone?",
      message: `"${m.date} — ${m.text}"\n\nThis cannot be undone.`,
    });
    if (!ok) return;
    await api.delete(`/milestones/${i}`);
    onChange({ ...state, milestones: milestones.filter((_, j) => j !== i) });
  };
  const addMile = async () => {
    if (!newMileDraft.date.trim()) return;
    const { data } = await api.post("/milestones", newMileDraft);
    onChange({ ...state, milestones: [...milestones, data] });
    setNewMile(false);
    setNewMileDraft({ date: "", text: "" });
  };

  // ---------- Render ----------
  return (
    <div className="view view-home" id="viewHome" data-testid="home-view">
      <div className="home-grid">
        <div className="home-main">
      {/* CORE QUESTION */}
      <div className="ctx-block" data-home-block="core">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3>The core question</h3>
          {isAdmin && !editingCore && (
            <button
              className="edit-pencil"
              onClick={() => {
                setCoreDraft(state.coreQuestion || "");
                setEditingCore(true);
              }}
              data-testid="core-question-edit"
              title="Edit"
            >
              ✎
            </button>
          )}
        </div>
        {editingCore ? (
          <>
            <textarea
              className="inline-edit"
              value={coreDraft}
              onChange={(e) => setCoreDraft(e.target.value)}
              style={{ minHeight: 100 }}
              data-testid="core-question-input"
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-sm" onClick={saveCore} data-testid="core-question-save">
                Save
              </button>
              <button
                className="btn btn-sm"
                style={{ background: "var(--bg-input)", color: "var(--text-dim)" }}
                onClick={() => setEditingCore(false)}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <p>{state.coreQuestion}</p>
        )}
      </div>

      {/* MILESTONES */}
      <div className="ctx-block">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3>Project Milestones</h3>
          {isAdmin && !newMile && (
            <button
              className="btn btn-sm"
              onClick={() => setNewMile(true)}
              data-testid="mile-add-toggle"
            >
              + Add
            </button>
          )}
        </div>
        {newMile && (
          <div style={{ background: "var(--bg-raised)", border: "1px solid var(--accent)", borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <input
              className="inline-edit"
              placeholder="Date label (e.g. 'June 23')"
              value={newMileDraft.date}
              onChange={(e) => setNewMileDraft({ ...newMileDraft, date: e.target.value })}
              data-testid="mile-new-date"
            />
            <input
              className="inline-edit"
              placeholder="Milestone description"
              value={newMileDraft.text}
              onChange={(e) => setNewMileDraft({ ...newMileDraft, text: e.target.value })}
              data-testid="mile-new-text"
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-sm" onClick={addMile} data-testid="mile-new-save">Add</button>
              <button
                className="btn btn-sm"
                style={{ background: "var(--bg-input)", color: "var(--text-dim)" }}
                onClick={() => setNewMile(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {milestones.map((m, i) =>
          editMileIdx === i ? (
            <div
              key={m.id || i}
              style={{ background: "var(--bg-raised)", border: "1px solid var(--accent)", borderRadius: 8, padding: 12, marginBottom: 8 }}
              data-testid={`mile-${i}-edit`}
            >
              <input
                className="inline-edit"
                value={mileDraft.date}
                onChange={(e) => setMileDraft({ ...mileDraft, date: e.target.value })}
              />
              <input
                className="inline-edit"
                value={mileDraft.text}
                onChange={(e) => setMileDraft({ ...mileDraft, text: e.target.value })}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-sm" onClick={saveMile}>Save</button>
                <button
                  className="btn btn-sm"
                  style={{ background: "var(--bg-input)", color: "var(--text-dim)" }}
                  onClick={() => setEditMileIdx(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="milestone" key={m.id || i} data-testid={`mile-${i}`}>
              <span className="milestone-date">{m.date}</span>
              <span className="milestone-text" style={{ flex: 1 }}>{m.text}</span>
              {isAdmin && (
                <>
                  <button
                    className="edit-pencil"
                    onClick={() => startEditMile(i)}
                    data-testid={`mile-${i}-edit`}
                  >
                    ✎
                  </button>
                  <button
                    className="del-x"
                    onClick={() => delMile(i)}
                    data-testid={`mile-${i}-delete`}
                  >
                    ×
                  </button>
                </>
              )}
            </div>
          )
        )}
      </div>

      {/* TASK COUNTS */}
      <div className="ctx-block tasks-summary" data-home-block="tasks" data-testid="home-tasks-summary">
        <div className="tasks-stats">
          <button
            type="button"
            className="tasks-stat"
            onClick={() => onJump?.({ type: "task.add", target: { kind: "task" } })}
            data-testid="home-tasks-outstanding"
          >
            <div className="tasks-stat-num">{(state.tasks || []).filter((t) => !t.done).length}</div>
            <div className="tasks-stat-label">Tasks outstanding</div>
          </button>
          <button
            type="button"
            className="tasks-stat tasks-stat-done"
            onClick={() => onJump?.({ type: "task.done", target: { kind: "task" } })}
            data-testid="home-tasks-completed"
          >
            <div className="tasks-stat-num">{(state.tasks || []).filter((t) => t.done).length}</div>
            <div className="tasks-stat-label">Completed all-time</div>
          </button>
        </div>
      </div>

      {/* PHASES */}
      <div className="sec-head" style={{ marginTop: 30 }}>The three phases</div>
      <div className="sec-desc">
        Phase {(state.phases.find((p) => p.state === "current") || {}).id ?? 1} is active. Click into it for the live dashboard.
      </div>
      <div className="phase-list" id="phaseList">
        {state.phases.map((p) => {
          const isCurrent = p.state === "current";
          const isComplete = p.state === "complete";
          const wpTasks = nodesForPhase(state.workplan, p.id).filter((n) => n.level === "task");
          const wpPct = wpTasks.length
            ? Math.round((wpTasks.filter((t) => t.status === "done").length / wpTasks.length) * 100)
            : 0;
          const phasePctVal = p.id === 1 ? pct : wpPct;
          const showBar = isCurrent || isComplete;
          return (
            <div
              key={p.id}
              className={`phase-card clickable ${isCurrent ? "current" : isComplete ? "complete" : "locked"}`}
              onClick={() => enterPhase(p.id)}
              data-testid={`phase-card-${p.id}`}
            >
              <div className="phase-idx">{p.id}</div>
              <div className="phase-info">
                <div className="phase-name">{p.name}</div>
                <div className="phase-meta">{p.meta}</div>
                <div className="phase-goal">{p.goal}</div>
                <div className="enter-hint">
                  {isCurrent ? "Enter dashboard \u2192" : isComplete ? "View phase \u2192" : "View phase \u2192"}
                </div>
              </div>
              <div className="phase-right">
                {showBar ? (
                  <>
                    <div className="phase-pct">{phasePctVal}%</div>
                    <div className="phase-bar-track">
                      <div
                        className="phase-bar-fill"
                        style={{ width: `${phasePctVal}%` }}
                      />
                    </div>
                    {isComplete && <div className="phase-status-tag">Complete</div>}
                  </>
                ) : (
                  <div className="phase-status-tag">Locked</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
        </div>{/* /home-main */}
        <div className="home-rail">
          <ActivityFeed limit={10} onJump={onJump} />
        </div>
      </div>{/* /home-grid */}
    </div>
  );
}
